import React, { createContext, useContext, useState, useEffect } from "react";
import authService from "../services/authService";
import rolesAndPermissions from "../config/rolesAndPermissions";

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restore from localStorage immediately
    const storedUser = authService.getStoredUser();
    const storedToken = authService.getStoredToken();
    const storedRefreshToken = localStorage.getItem("refreshToken");

    console.log("Restored user from localStorage:", storedUser);
    console.log("Stored token exists:", !!storedToken);
    console.log("Stored refresh token exists:", !!storedRefreshToken);

    if (storedUser) setUser(storedUser);
    if (storedToken) setToken(storedToken);

    async function tryRefresh() {
      setLoading(true);

      // Only attempt refresh if we have both a token and refresh token
      if (storedToken && storedRefreshToken) {
        try {
          const newToken = await authService.refreshToken();
          if (newToken) {
            setToken(newToken);
            setIsAuthenticated(true);
            const userInfo = await authService.getUserInfo();
            setUser(userInfo);
            console.log("User info from /auth/me:", userInfo);
            authService.setStoredAuth(newToken, userInfo);
          } else {
            // Refresh failed, clear everything
            setToken(null);
            setIsAuthenticated(false);
            setUser(null);
            authService.logout();
          }
        } catch (error) {
          console.error("Refresh failed:", error);
          setToken(null);
          setIsAuthenticated(false);
          setUser(null);
          authService.logout();
        }
      } else {
        // No tokens available, just set loading to false
        setIsAuthenticated(false);
      }

      setLoading(false);
    }

    tryRefresh();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);

      if (response.user && response.token) {
        setUser(response.user);
        setToken(response.token);
        setIsAuthenticated(true);

        // Store in localStorage
        authService.setStoredAuth(response.token, response.user);

        // Clear guardian-specific localStorage flag for non-guardian users
        if (response.user.role !== 'GUARDIAN') {
          localStorage.removeItem('guardian_awaiting_key_collection');
        }

        return { success: true };
      }
      return { success: false, error: "Login failed" };
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    console.log('🚪 [AUTH] Logging out user...');
    
    // First, clear all auth-related local storage items
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    
    // Clear any other potential auth-related items
    localStorage.removeItem('darkMode');
    localStorage.removeItem('_grecaptcha');
    localStorage.removeItem('intercom.intercom-state-shwpcdf5');
    localStorage.removeItem('present-aokmtkt893@iemail.one');
    
    // Clear filter preservation states (Laravel session equivalent)
    // Tickets page filters
    localStorage.removeItem('tickets_filters');
    localStorage.removeItem('tickets_scroll_position');
    localStorage.removeItem('tickets_current_page');

    // Clear guardian-specific state (used for Navbar menu)
    localStorage.removeItem('guardian_awaiting_key_collection');

    // Clear any other saved form states (Laravel: clear all session data on logout)
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      // Clear any ticket_edit_form states
      if (key && key.startsWith('ticket_edit_form_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    console.log('🧹 [AUTH] Local storage cleared (including filter states)');
    
    // Clear state
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    
    console.log('🔄 [AUTH] State cleared');
    
    // Call authService logout for any backend cleanup
    authService.logout();
    
    console.log('🌐 [AUTH] Redirecting to login page...');
    
    // Navigate to login page
    window.location.href = '/login';
  };

  // Enhanced permission checking with role hierarchy - Laravel equivalent
  const hasPermission = (permission) => {
    console.log('🔐 [AUTH] Checking permission:', permission);
    
    if (!user) {
      console.log('❌ [AUTH] No user found');
      return false;
    }

    const userPermissions = Array.isArray(user.permissions) ? user.permissions : [];
    const userRoles = Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []);
    
    console.log('🔐 [AUTH] User permissions:', userPermissions);
    console.log('🔐 [AUTH] User roles:', userRoles);
    
    // 1. Check explicit permission first
    if (userPermissions.includes(permission)) {
      console.log('✅ [AUTH] User has explicit permission:', permission);
      return true;
    }
    
    // 2. Check for wildcard permissions (System Admin)
    if (userPermissions.includes('*')) {
      console.log('✅ [AUTH] User has wildcard permission');
      return true;
    }
    
    // 3. Check for module wildcard (e.g., 'properties.*')
    const module = permission.split('.')[0];
    if (userPermissions.includes(`${module}.*`)) {
      console.log('✅ [AUTH] User has module wildcard:', `${module}.*`);
      return true;
    }
    
    // 4. Role hierarchy fallback - Laravel equivalent role-based permissions
    if (userRoles.length > 0) {
      const maxUserLevel = Math.max(
        ...userRoles.map(roleName => rolesAndPermissions.getRoleLevel(roleName))
      );
      
      console.log('🔐 [AUTH] User max role level:', maxUserLevel);
      
      // System Administrator (Level 9) - Full Access
      if (maxUserLevel >= 9) {
        console.log('✅ [AUTH] System Administrator - full access');
        return true;
      }
      
      // Senior Management (Level 8) - Near Full Access
      if (maxUserLevel >= 8) {
        const seniorManagementPermissions = [
          'properties.edit', 'properties.show', 'properties.create', 'properties.delete',
          'guardians.edit', 'guardians.show', 'guardians.create',
          'payments.edit', 'payments.show', 'payments.list',
          'users.edit', 'users.show', 'reports.view', 'settings.view',
          'roles.view', 'permissions.view',
          'documents.archive', 'documents.delete'
        ];
        if (seniorManagementPermissions.includes(permission)) {
          console.log('✅ [AUTH] Senior Management role grants access to:', permission);
          return true;
        }
      }
      
      // Management/Admin Level (Level 7) - Department Management
      if (maxUserLevel >= 7) {
        const adminPermissions = [
          'properties.edit', 'properties.show', 'properties.create',
          'guardians.edit', 'guardians.show', 'payments.edit', 'reports.view'
        ];
        if (adminPermissions.includes(permission)) {
          console.log('✅ [AUTH] Admin role grants access to:', permission);
          return true;
        }
      }
      
      // Management Level (Level 5-6) - Team Management
      if (maxUserLevel >= 5) {
        const managementPermissions = [
          'properties.edit', 'properties.show', 'guardians.edit', 
          'guardians.show', 'payments.view', 'payments.list'
        ];
        if (managementPermissions.includes(permission)) {
          console.log('✅ [AUTH] Management role grants access to:', permission);
          return true;
        }
      }
      
      // Staff Level (Level 3-4) - Operational Access
      if (maxUserLevel >= 3) {
        const staffPermissions = [
          'properties.show', 'guardians.show', 'payments.view',
          'property_viewings.show', 'inspections.show'
        ];
        if (staffPermissions.includes(permission)) {
          console.log('✅ [AUTH] Staff role grants access to:', permission);
          return true;
        }
      }
      
      // Client/Guardian Level (Level 1-2) - Limited Access
      if (maxUserLevel >= 1) {
        const clientGuardianPermissions = [
          'spaces.available', 'spaces.show', 'property_viewings.attend',
          'property_viewings.keep_informed'
        ];
        if (clientGuardianPermissions.includes(permission)) {
          console.log('✅ [AUTH] Client/Guardian role grants access to:', permission);
          return true;
        }
      }
    }
    
    console.log('❌ [AUTH] Access denied for permission:', permission);
    return false;
  };

  // Role hierarchy methods - Laravel equivalent
  const hasRoleLevel = (minimumLevel) => {
    if (!user) return false;
    const userRoles = Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []);
    return rolesAndPermissions.hasRoleLevel(userRoles, minimumLevel);
  };

  const hasRoleOrHigher = (targetRole) => {
    if (!user) return false;
    const userRoles = Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []);
    return rolesAndPermissions.hasRoleOrHigher(userRoles, targetRole);
  };

  const isAdminRole = () => {
    if (!user) return false;
    const userRoles = Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []);
    return userRoles.some(roleName => rolesAndPermissions.isAdminRole(roleName));
  };

  const isManagementRole = () => {
    if (!user) return false;
    const userRoles = Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []);
    return userRoles.some(roleName => rolesAndPermissions.isManagementRole(roleName));
  };

  const isStaffRole = () => {
    if (!user) return false;
    const userRoles = Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []);
    return userRoles.some(roleName => rolesAndPermissions.isStaffRole(roleName));
  };

  const getUserRoleLevel = () => {
    if (!user) return -1;
    const userRoles = Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []);
    return Math.max(...userRoles.map(roleName => rolesAndPermissions.getRoleLevel(roleName)));
  };

  const getRoleDisplayName = (roleName) => {
    return rolesAndPermissions.getRoleDisplayName(roleName || user?.role);
  };

  // Contractor-specific role checking - Laravel equivalent
  const isContractor = () => {
    if (!user) return false;
    const userRole = user.role?.name || user.role;
    return userRole === 'CONTRACTOR' || userRole === 'CONTRACTOR-READ-ONLY';
  };

  const isContractorReadOnly = () => {
    if (!user) return false;
    const userRole = user.role?.name || user.role;
    return userRole === 'CONTRACTOR-READ-ONLY';
  };

  const getContractorContactId = () => {
    if (!user || !isContractor()) return null;
    // Laravel uses userable_id for contractor contact ID
    return user.userable_id || user.id;
  };

  // Guardian-specific role checking - Laravel equivalent
  const isGuardian = () => {
    if (!user) return false;
    const userRole = user.role?.name || user.role;
    // Check both role name and userable_type for guardian identification
    const isGuardianRole = userRole === 'GUARDIAN';
    const isGuardianType = user.userable_type === 'App\\Guardians\\Guardian' || 
                           user.userable_type === 'Guardian' || 
                           user.userable_type === 'guardians';
    return isGuardianRole || isGuardianType;
  };

  const getGuardianId = () => {
    if (!user || !isGuardian()) return null;
    // Laravel uses userable_id for guardian ID
    return user.userable_id || user.id;
  };

  // Guardian Manager role checking - Laravel equivalent
  const isGuardianManager = () => {
    if (!user) return false;
    const userRole = user.role?.name || user.role;
    // Check for all GUARDIAN-MANAGER variants
    return (
      userRole === 'GUARDIAN-MANAGER' ||
      userRole === 'GUARDIAN-MANAGER-WITH-MAINTENANCE' ||
      userRole === 'GUARDIAN-MAINTENANCE-CLIENT' ||
      userRole === 'GUARDIAN-MANAGER-WITH-PROPERTIES-EDIT'
    );
  };

  // Guardian Manager With Maintenance - properties-only access
  const isGuardianManagerWithMaintenance = () => {
    if (!user) return false;
    const userRole = user.role?.name || user.role;
    return userRole === 'GUARDIAN-MANAGER-WITH-MAINTENANCE';
  };

  // Client-specific role checking - Laravel equivalent
  const isClient = () => {
    if (!user) return false;
    const userRole = user.role?.name || user.role;
    // Check both role name and userable_type for client identification
    const isClientRole = userRole === 'CLIENT';
    const isClientContactType = user.userable_type === 'App\\Clients\\ClientContact' ||
                                user.userable_type === 'ClientContact' ||
                                user.userable_type === 'clientscontacts' ||
                                user.userable_type === 'clients_contacts';
    return isClientRole || isClientContactType;
  };

  // Get client ID from user (CLIENT users are typically client contacts)
  // Returns the client_id that the user belongs to
  const getClientId = async () => {
    if (!user || !isClient()) return null;

    // If user is a client contact, we need to get the parent client_id
    // This requires an async call to get the client contact's client_id
    // For now, return userable_id as a fallback (will be resolved in backend)
    return user.userable_id || null;
  };

  // Synchronous version that returns userable_id (for route restrictions)
  const getClientContactId = () => {
    if (!user || !isClient()) return null;
    return user.userable_id || null;
  };

  // Sales With Guardians role checking - Laravel equivalent
  const isSalesWithGuardians = () => {
    if (!user) return false;
    const userRole = user.role?.name || user.role;
    return userRole === 'SALES WITH GUARDIANS';
  };

  // Merge fields into the current user (state + localStorage) so UI that gates on user
  // fields — e.g. the certificate-review nav item — updates live without a re-login.
  const updateUser = (patch) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...patch };
      try {
        localStorage.setItem("user", JSON.stringify(next));
      } catch (e) {
        /* ignore storage errors */
      }
      return next;
    });
  };

  const value = {
    user,
    token,
    login,
    logout,
    updateUser,
    isAuthenticated,
    loading,
    hasPermission,
    hasRoleLevel,
    hasRoleOrHigher,
    isAdminRole,
    isManagementRole,
    isStaffRole,
    getUserRoleLevel,
    getRoleDisplayName,
    isContractor,
    isContractorReadOnly,
    getContractorContactId,
    isGuardian,
    getGuardianId,
    isGuardianManager,
    isGuardianManagerWithMaintenance,
    isClient,
    getClientId,
    getClientContactId,
    isSalesWithGuardians,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
